import '@github/relative-time-element';
import { AlertsElement, MenuElement, PopupElement, TooltipElement } from 'inclusive-elements';

import { enableGlassStretch } from './behaviours/glassStretch.ts';
import { enableInputBlurring } from './behaviours/inputBlurring.ts';
import { enablePopups } from './behaviours/popups.ts';
import { enableSheetDismiss, Modal } from './behaviours/sheetDismiss.ts';
import { enableTapCancelling } from './behaviours/tapCancelling.ts';

customElements.define('ui-alerts', AlertsElement);
customElements.define('ui-menu', MenuElement);
customElements.define('ui-modal', Modal);
customElements.define('ui-popup', PopupElement);
customElements.define('ui-tooltip', TooltipElement);

AlertsElement.duration = 5000;

// Record the scrollbar's width, so _Modal.less can pad the page while a modal
// hides it, and the page's width for fixed sidebars, as some browsers change
// viewport units when the scrollbar hides.
//
// Neither is remeasured while a modal is open, which would skip its enter
// transition, except the first time, as sidebars need a width. Unchanged
// writes are skipped, as each restyles the whole document.
new ResizeObserver(() => {
    const root = document.documentElement;
    const measured = root.style.getPropertyValue('--page-width');
    if (measured && document.querySelector('ui-modal:is([open], .leave-active)')) return;

    for (const [name, value] of [
        ['--scrollbar-width', `${innerWidth - root.clientWidth}px`],
        ['--page-width', `${root.clientWidth}px`],
    ]) {
        if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value);
    }
}).observe(document.documentElement);

// Allow iOS Safari to have :active state on elements
document.addEventListener('touchstart', () => {}, { passive: true });

// On iOS, a control stays :active and looks pressed when a touch on it turns
// into a scroll, which sends pointercancel. So mark the root to turn press
// effects off until the next press. That restyles every control, so it's only
// done when the scroll started on one.
const PRESSABLE = 'a, button, label, [role=button], [role=menuitem], .clickable, .glass, .Menu';
const { classList } = document.documentElement;
document.addEventListener(
    'pointercancel',
    (event) => {
        if ((event.target as Element).closest?.(PRESSABLE)) classList.add('is-scrolling');
    },
    { passive: true },
);
document.addEventListener(
    'pointerdown',
    () => {
        if (classList.contains('is-scrolling')) classList.remove('is-scrolling');
    },
    { passive: true },
);

enableInputBlurring();
enableSheetDismiss();
enableGlassStretch();
enablePopups();
enableTapCancelling();
